import { useState, useRef } from "react";
import { toast } from "react-toastify";

const DragDropUpload = ({
  onFileSelect,
  accept = "image/*",
  maxSize = 5 * 1024 * 1024, // 5MB default
  className = "",
  children,
  disabled = false,
  showPreview = false,
  previewClassName = "",
  hasFile = false, // New prop to indicate if file is selected
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileValidation(droppedFile);
    }
  };

  const handleFileValidation = (file) => {
    // Check file type - handle both MIME types and file extensions
    const isValidType = () => {
      // If accept is "*", allow all files
      if (accept === "*") return true;

      // Check MIME type first
      if (file.type && file.type !== "application/octet-stream") {
        const mimeTypes = accept.split(",").map((type) => type.trim());
        if (
          mimeTypes.some((type) => file.type.match(type.replace("*", ".*")))
        ) {
          return true;
        }
      }

      // Check file extension as fallback
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));
      const acceptedExtensions = accept
        .split(",")
        .map((type) => type.trim().toLowerCase());

      return acceptedExtensions.some((ext) => {
        // Handle both .ext and ext formats
        if (ext.startsWith(".")) {
          return ext === fileExtension;
        } else {
          return `.${ext}` === fileExtension;
        }
      });
    };

    if (!isValidType()) {
      toast.error(`Please select a valid file type. Accepted: ${accept}`);
      return;
    }

    // Check file size
    if (file.size > maxSize) {
      toast.error(
        `File size should be less than ${(maxSize / 1024 / 1024).toFixed(1)}MB`
      );
      return;
    }

    // Create preview if it's an image and preview is enabled
    if (showPreview && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }

    // Call the parent's onFileSelect callback
    onFileSelect(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileValidation(file);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const clearPreview = () => {
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      <div
        className={`relative border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ${
          hasFile
            ? "border-green-500 bg-green-50"
            : isDragOver
            ? "border-blue-500 bg-blue-50"
            : disabled
            ? "border-gray-300 bg-gray-100 cursor-not-allowed"
            : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-gray-100"
        } ${className}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />

        {children || (
          <div className="flex flex-col items-center justify-center p-8">
            {hasFile ? (
              <svg
                className="w-12 h-12 mb-4 text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                className={`w-12 h-12 mb-4 ${
                  isDragOver ? "text-blue-500" : "text-gray-400"
                }`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                />
              </svg>
            )}
            <div className="text-center">
              {hasFile ? (
                <>
                  <p className="text-sm font-semibold text-green-600 mb-1">
                    File uploaded successfully
                  </p>
                  <p className="text-xs text-gray-500">
                    Click to change file or drag and drop a new one
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium text-blue-600">
                      Click to upload
                    </span>{" "}
                    or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {accept === "image/*" ? "Images" : "Files"} up to{" "}
                    {(maxSize / 1024 / 1024).toFixed(1)}MB
                  </p>
                  {isDragOver && (
                    <p className="text-xs text-blue-500 font-semibold mt-2">
                      Drop your file here
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DragDropUpload;
